import { AssetTypes } from '../../../../../types/asset-types.types'
import { CENTER_IMAGE_SIZE_ERROR, MAX_CENTER_IMAGE_SIZE } from '../../../../../constants/flowcode'
import { FcShape } from '@dtx-company/flowcode-generator-types/src'
import { dataURItoBlob } from '../../../../../utils/file-manipulation'
import { getBase64Strings } from 'exif-rotate-js'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { uploadExternalCenterImageFile } from '../../../../../utils/flowcode'
import { useCallback } from 'react'
import { useCreateFlowcodeState } from '../../../../../hooks/useCreateFlowcodeState'

export function useOnChangeCenterImage(
  setCenterImage: (file: File) => void
): (e: React.ChangeEvent<HTMLInputElement>) => Promise<void> {
  return useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files && e.target.files[0]
      if (file && file.size > MAX_CENTER_IMAGE_SIZE) {
        sendErrorNotification(CENTER_IMAGE_SIZE_ERROR.message)
      } else {
        if (file?.type === 'image/jpeg') {
          const fileData = await getBase64Strings([file as Blob])
          const adjustedBlob = dataURItoBlob(fileData[0])
          const adjustedFile = new File([adjustedBlob], file?.name || 'file.jpeg', {
            type: 'image/jpeg'
          })
          setCenterImage(adjustedFile)
        } else if (file) {
          setCenterImage(file)
        }
      }
    },
    [setCenterImage]
  )
}

// Used to handle center image uploads for square codes.
// Square codes don't support blob image formats so we upload the image to the CDN instead
export function useOnChangeSquareCodeCenterImage(
  setCenterImage: (file: File, externalCenterImageUrl?: string) => void
): (e: React.ChangeEvent<HTMLInputElement>) => Promise<void> {
  return useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files && e.target.files[0]
      if (file && file.size > MAX_CENTER_IMAGE_SIZE) {
        sendErrorNotification(CENTER_IMAGE_SIZE_ERROR.message)
      } else {
        if (file) {
          const centerImageFile = await uploadExternalCenterImageFile({
            fileData: file,
            assetType: AssetTypes.EXTERNAL_CENTER_IMAGE
          })
          setCenterImage(file, centerImageFile.file)
        }
      }
    },
    [setCenterImage]
  )
}

export function useHandleCenterImageChange(
  setCenterImage: (file: File, externalCenterImageUrl?: string) => void
): (e: React.ChangeEvent<HTMLInputElement>) => Promise<void> {
  const { borderShape } = useCreateFlowcodeState()
  const onChangeCenterImage = useOnChangeCenterImage(setCenterImage)
  const onChangeSquareCodeCenterImage = useOnChangeSquareCodeCenterImage(setCenterImage)

  return borderShape === FcShape.SQUARE ? onChangeSquareCodeCenterImage : onChangeCenterImage
}
